import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Menu,
  MenuItem,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ArticleIcon from "@mui/icons-material/Article";
import { useNavigate, useLocation } from "react-router-dom";

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const isActive = (path) => location.pathname === path;

  const handleNav = (path) => {
    navigate(path);
    setOpen(false);
    setAnchorEl(null);
  };

  // ✅ Articles dropdown items (SEO + internal linking)
  const articleItems = [
    { label: "Bride Biodata Format", path: "/articles/biodata-format-for-bride" },
    { label: "Groom Biodata Format", path: "/articles/biodata-format-for-groom" },
    { label: "Marathi Biodata", path: "/articles/marathi-marriage-biodata-format" },
    { label: "Hindi Biodata", path: "/articles/hindi-marriage-biodata-format" },
  ];

  return (
    <>
      {/* ================= NAVBAR ================= */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: "linear-gradient(90deg, #f57c00, #ef6c00)",
          borderBottom: "1px solid rgba(0,0,0,0.1)",
        }}
      >
        <Toolbar sx={{ minHeight: 70 }}>
          {/* ===== LOGO + BRAND ===== */}
          <Box
            onClick={() => handleNav("/")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexGrow: 1,
            }}
          >
            <Box
              component="img"
              src="/MarriageLogo.png"
              sx={{
                height: 34,
                filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
              }}
            />

            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1rem",
                color: "#fff",
              }}
            >
              MarriageBiodata.live
            </Typography>
          </Box>

          {/* ===== DESKTOP MENU ===== */}
          <Box sx={{ display: { xs: "none", md: "flex" }, gap: 2 }}>
            <Button
              onClick={() => handleNav("/")}
              sx={navStyle(isActive("/"))}
            >
              Home
            </Button>

            {/* ===== Articles Dropdown ===== */}
            <Button
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={navStyle(location.pathname.includes("/articles"))}
              startIcon={<ArticleIcon />}
            >
              Articles
            </Button>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
            >
              {articleItems.map((item) => (
                <MenuItem
                  key={item.path}
                  onClick={() => handleNav(item.path)}
                >
                  {item.label}
                </MenuItem>
              ))}
            </Menu>

            <Button
              onClick={() => handleNav("/marriage-biodata-templates")}
              sx={navStyle(isActive("/marriage-biodata-templates"))}
            >
              Templates
            </Button>

            <Button
              onClick={() => handleNav("/about")}
              sx={navStyle(isActive("/about"))}
            >
              About
            </Button>

            <Button
              onClick={() => handleNav("/contact-us")}
              sx={navStyle(isActive("/contact-us"))}
            >
              Contact
            </Button>

            {/* ===== STRONG CTA ===== */}
            <Button
              onClick={() => handleNav("/create-biodata")}
              sx={{
                ml: 1,
                px: 3,
                borderRadius: 3,
                fontWeight: 700,
                textTransform: "none",
                background: "#ffffff",
                color: "#e65100",
                boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
                "&:hover": {
                  background: "#fff3e0",
                },
              }}
            >
              Create Biodata
            </Button>
          </Box>

          {/* ===== MOBILE MENU BUTTON ===== */}
          <IconButton
            onClick={() => setOpen(true)}
            sx={{
              display: { xs: "flex", md: "none" },
              bgcolor: "rgba(255,255,255,0.2)",
            }}
          >
            <MenuIcon sx={{ color: "#fff" }} />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* ================= MOBILE DRAWER ================= */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: { width: 280 },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "1rem",
              color: "#e65100",
              mb: 1,
            }}
          >
            MarriageBiodata.live
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <List>
            <DrawerItem label="Home" onClick={() => handleNav("/")} />
            <DrawerItem label="Articles" onClick={() => handleNav("/articles")} />
            <DrawerItem label="Templates" onClick={() => handleNav("/marriage-biodata-templates")} />

            {articleItems.map((item) => (
              <DrawerItem
                key={item.path}
                label={`• ${item.label}`}
                onClick={() => handleNav(item.path)}
              />
            ))}

            <DrawerItem label="About" onClick={() => handleNav("/about")} />
            <DrawerItem label="Contact" onClick={() => handleNav("/contact-us")} />

            <Divider sx={{ my: 1 }} />

            {/* CTA in mobile */}
            <DrawerItem
              label="Create Biodata"
              onClick={() => handleNav("/create-biodata")}
              bold
            />
          </List>
        </Box>
      </Drawer>
    </>
  );
}

/* ===== STYLES ===== */

const navStyle = (active) => ({
  fontWeight: active ? 700 : 600,
  textTransform: "none",
  color: "#fff",
  borderBottom: active ? "2px solid #fff" : "2px solid transparent",
  borderRadius: 0,
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.15)",
  },
});

/* ===== Drawer Item Component ===== */
const DrawerItem = ({ label, onClick, bold }) => (
  <ListItem disablePadding sx={{ mb: 1 }}>
    <ListItemButton
      onClick={onClick}
      sx={{
        borderRadius: 2,
        "&:hover": { backgroundColor: "#fff3e0" },
      }}
    >
      <ListItemText
        primary={label}
        primaryTypographyProps={{
          fontWeight: bold ? 700 : 600,
        }}
      />
    </ListItemButton>
  </ListItem>
);